import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getCompanyIdForUser, findAccountIdByEmail } from '../services/firestoreService';

/**
 * Hook to get the current user's company/account ID for data attribution.
 * 
 * Use this for COMPANY-WIDE data (shared across all team members):
 * - Roadmap, tasks, questions, events, etc.
 * 
 * For USER-SPECIFIC data (per team member), use currentUser.uid:
 * - Course progress, notification preferences, profile settings, etc.
 * 
 * @returns {{ companyId: string|null, loading: boolean }}
 */
export const useCompanyId = () => {
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCompanyId = async () => {
      if (!currentUser?.uid) {
        setCompanyId(null);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // First try to resolve by user doc accountId/clientId (shared helper)
        let cid = await getCompanyIdForUser(currentUser.uid);

        // If not found, fall back to accounts lookup by email (like JobDescriptions)
        if (!cid && currentUser.email) {
          try {
            cid = await findAccountIdByEmail(currentUser.email, 'Clients');
          } catch (findErr) {
            console.error('Error finding account by email for company ID:', findErr);
          }
        }

        if (!cancelled) {
          setCompanyId(cid || null);
        }
      } catch (error) {
        console.error('Error loading company ID:', error);
        if (!cancelled) {
          setCompanyId(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCompanyId();

    return () => {
      cancelled = true;
    };
  }, [currentUser?.uid, currentUser?.email]);

  return { companyId, loading };
};
